import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getPlayerId, setPlayerId } from '../utils';
import { apiFetch, apiUrl, setAuthTokenProvider } from "../apiBase";
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";

// ============================================
// AUTHENTICATION STORE
// ============================================

/**
 * Sincroniza o PLAYER_ID local com o odysId autenticado.
 * Isso garante cross-save: o progresso será salvo/carregado com o ID correto.
 * Também recarrega o progresso e perfil do servidor se o ID mudou.
 */
const syncPlayerIdWithAuth = async (odysId: string) => {
  const currentPlayerId = getPlayerId();
  if (currentPlayerId !== odysId) {
    console.log(`[Auth] Sincronizando Player ID: ${currentPlayerId} -> ${odysId}`);
    setPlayerId(odysId);
    
    // Recarregar progresso e perfil do servidor com o novo ID
    // Importação dinâmica para evitar dependência circular
    try {
      const { useProgress } = await import('./useProgress');
      const { useProfile } = await import('./useProfile');
      
      console.log('[Auth] Recarregando dados do servidor com novo ID...');
      await Promise.all([
        useProgress.getState().loadFromServer(),
        useProfile.getState().loadFromServer(),
      ]);
      console.log('[Auth] Dados sincronizados com sucesso!');
    } catch (error) {
      console.error('[Auth] Erro ao recarregar dados:', error);
    }
  }
};

export type AuthProvider = 'google' | 'discord' | 'apple' | 'email' | 'guest';

/**
 * Lei Felca (Lei 15.211/2025): status de proteção calculado pelo servidor.
 * minorProtected = conta tratada como de menor (proteções por padrão).
 */
export interface ProtectionStatus {
  minorProtected: boolean;
  ageBracket: 'under13' | '13_15' | '16_17' | 'adult' | 'unknown';
  ageSource: 'verified_signal' | 'self_declared' | 'none';
  guardianStatus: 'none' | 'pending' | 'active' | 'revoked';
  guardianEmailMasked: string | null;
  purchasesAllowed: boolean;
  chatAllowed: boolean;
}

/** Default protetivo usado quando o servidor ainda não informou o status. */
export const PROTECTED_DEFAULT: ProtectionStatus = {
  minorProtected: true,
  ageBracket: 'unknown',
  ageSource: 'none',
  guardianStatus: 'none',
  guardianEmailMasked: null,
  purchasesAllowed: false,
  chatAllowed: false,
};

export interface User {
  id: string;
  odysId: string; // Legacy ID for backwards compatibility
  provider: AuthProvider;
  email?: string;
  displayName: string;
  avatarUrl?: string;
  fullName?: string | null;
  alias?: string | null;
  birthDate?: string | null;
  createdAt: number;
  lastLoginAt: number;
  isVerified: boolean;
  // Game data
  linkedAccounts: AuthProvider[];
  isPremium: boolean;
  premiumUntil?: number;
  // Lei Felca: status de proteção (pode faltar em respostas antigas do servidor)
  protection?: ProtectionStatus;
}

/**
 * Helper central: status de proteção efetivo de um usuário.
 * Sem informação do servidor → default protetivo (menor por padrão).
 */
export function getEffectiveProtection(user: User | null | undefined): ProtectionStatus {
  return user?.protection ?? PROTECTED_DEFAULT;
}

export interface AuthState {
  // Current user
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  
  // Guest mode (legacy)
  guestId: string;
  isGuest: boolean;
  
  // Actions
  loginWithGoogle: () => Promise<void>;
  loginWithDiscord: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  requestEmailLoginCode: (email: string) => Promise<void>;
  verifyEmailLoginCode: (email: string, code: string) => Promise<void>;
  registerGuestProfile: (payload: { email: string; fullName: string; alias: string; birthDate: string }) => Promise<void>;
  continueAsGuest: () => void;
  logout: () => void;
  linkAccount: (provider: AuthProvider) => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  
  // Migration
  migrateGuestToAccount: () => Promise<boolean>;
  updateProfile: (data: Partial<User>) => void;
}

const getDeviceInfo = () => {
  const storageKey = "rocketanks_device_id";
  let deviceId = localStorage.getItem(storageKey);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(storageKey, deviceId);
  }

  const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web";
  const deviceName = navigator.userAgent || "unknown";

  return { deviceId, platform, deviceName };
};

let _socialLoginInitialized = false;
const ensureSocialLogin = async () => {
  if (!_socialLoginInitialized) {
    // Guard explícito: quando o plugin nativo não foi compilado no APK
    // (falta de `npx cap sync android` antes do build), qualquer chamada ao
    // proxy falha com o erro críptico "SocialLogin.then() is not implemented
    // on android". Detecta e explica em vez de quebrar o fluxo de login.
    if (Capacitor.isNativePlatform() && !Capacitor.isPluginAvailable("SocialLogin")) {
      throw new Error(
        "Login Google indisponível neste build do app (plugin nativo ausente). " +
        "Recompile com `npx cap sync android` antes do build do APK.",
      );
    }
    const webClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID || "";
    if (!webClientId) {
      throw new Error("VITE_GOOGLE_WEB_CLIENT_ID não configurado");
    }
    await SocialLogin.initialize({
      google: {
        webClientId,
      },
    });
    _socialLoginInitialized = true;
  }
};

const getErrorMessage = async (response: Response, fallback: string) => {
  try {
    const data = await response.json();
    return data?.error || data?.message || fallback;
  } catch {
    try {
      const text = await response.text();
      return text ? `${fallback}: ${text}` : fallback;
    } catch {
      return fallback;
    }
  }
};

type OAuthCallbackPayload = {
  type: 'oauth_callback';
  code?: string;
  state?: string;
  error?: string;
};

const OAUTH_CALLBACK_CHANNEL = 'rocketanks_oauth_callback';
const OAUTH_CALLBACK_STORAGE_KEY = 'rocketanks_oauth_callback';

const isOAuthCallbackPayload = (value: unknown): value is OAuthCallbackPayload => {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { type?: unknown }).type === 'oauth_callback'
  );
};

/**
 * OAuth helper - abre fluxo de autenticacao.
 * Web: usa popup (window.open)
 * Capacitor: usa @capacitor/browser (abre browser externo do sistema)
 */
const openOAuthPopup = (url: string, name: string): Promise<{ code: string; state: string }> => {
  // No Capacitor, usar @capacitor/browser + deep link para retorno
  if (Capacitor.isNativePlatform()) {
    return openOAuthBrowser(url);
  }

  // Web: popup tradicional
  return new Promise((resolve, reject) => {
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;
    let popup: Window | null = null;
    let pollTimer: number | null = null;
    let timeoutTimer: number | null = null;
    let settled = false;
    let oauthChannel: BroadcastChannel | null = null;

    const getAllowedOrigins = () => {
      const origins = new Set<string>([window.location.origin]);
      try {
        origins.add(new URL(apiUrl("/")).origin);
      } catch {
        // apiUrl may be relative on web; current origin is already allowed.
      }
      return origins;
    };

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
      if (pollTimer !== null) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
      if (timeoutTimer !== null) {
        window.clearTimeout(timeoutTimer);
        timeoutTimer = null;
      }
      if (oauthChannel) {
        oauthChannel.close();
        oauthChannel = null;
      }
    };

    const finish = (payload: OAuthCallbackPayload) => {
      if (settled) return;
      settled = true;
      cleanup();
      try {
        popup?.close();
      } catch {
        // Ignore browsers that block closing the popup.
      }

      if (payload.error) {
        reject(new Error(payload.error));
        return;
      }

      if (!payload.code || !payload.state) {
        reject(new Error('Resposta OAuth inválida'));
        return;
      }

      resolve({ code: payload.code, state: payload.state });
    };

    const fail = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      try {
        popup?.close();
      } catch {
        // Ignore browsers that block closing the popup.
      }
      reject(error);
    };

    const handlePayload = (payload: unknown) => {
      if (!isOAuthCallbackPayload(payload)) return;
      finish(payload);
    };

    const handleMessage = (event: MessageEvent) => {
      if (!getAllowedOrigins().has(event.origin)) return;
      handlePayload(event.data);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== OAUTH_CALLBACK_STORAGE_KEY || !event.newValue) return;
      try {
        handlePayload(JSON.parse(event.newValue));
      } catch {
        // Ignore unrelated/corrupted storage events.
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);

    try {
      oauthChannel = new BroadcastChannel(OAUTH_CALLBACK_CHANNEL);
      oauthChannel.onmessage = (event) => handlePayload(event.data);
    } catch {
      oauthChannel = null;
    }

    popup = window.open(
      url,
      name,
      `width=${width},height=${height},left=${left},top=${top}`
    );

    if (!popup) {
      fail(new Error('Popup bloqueado! Habilite popups para fazer login.'));
      return;
    }

    // Poll for popup close (user cancelled)
    pollTimer = window.setInterval(() => {
      if (popup.closed) {
        if (pollTimer !== null) {
          window.clearInterval(pollTimer);
          pollTimer = null;
        }
        window.setTimeout(() => {
          fail(new Error('Login cancelado'));
        }, 750);
      }
    }, 500);

    timeoutTimer = window.setTimeout(() => {
      fail(new Error('Login expirou'));
    }, 120_000);
  });
};

/**
 * Abre OAuth via @capacitor/browser no mobile.
 * Escuta deep link de retorno (capacitor:urlOpen event).
 */
async function openOAuthBrowser(url: string): Promise<{ code: string; state: string }> {
  const { Browser } = await import("@capacitor/browser");

  return new Promise((resolve, reject) => {
    let resolved = false;
    let browserFinishedHandle: { remove: () => void } | null = null;

    // Cleanup centralizado - remove TODOS os listeners
    const cleanup = () => {
      window.removeEventListener("capacitor:urlOpen", handleUrlOpen);
      clearTimeout(timeout);
      if (browserFinishedHandle) {
        browserFinishedHandle.remove();
        browserFinishedHandle = null;
      }
    };

    // Escutar deep link de retorno
    const handleUrlOpen = (event: Event) => {
      const { url: returnUrl } = (event as CustomEvent).detail;
      if (!returnUrl || resolved) return;

      try {
        const parsed = new URL(returnUrl);
        const code = parsed.searchParams.get("code");
        const state = parsed.searchParams.get("state");

        if (code && state) {
          resolved = true;
          cleanup();
          Browser.close().catch(() => {});
          resolve({ code, state });
        }
      } catch {
        // URL invalida, ignorar
      }
    };

    window.addEventListener("capacitor:urlOpen", handleUrlOpen);

    // Timeout de 2 minutos
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        reject(new Error("Login expirou"));
      }
    }, 120_000);

    // Escutar fechamento do browser (usuario cancelou)
    Browser.addListener("browserFinished", () => {
      if (!resolved) {
        resolved = true;
        cleanup();
        reject(new Error("Login cancelado"));
      }
    }).then(handle => {
      browserFinishedHandle = handle;
      // Se ja resolveu antes do listener ser registrado, limpa imediatamente
      if (resolved) handle.remove();
    });

    // Abrir browser com URL absoluta
    Browser.open({ url: apiUrl(url) }).catch((err) => {
      resolved = true;
      cleanup();
      reject(err);
    });
  });
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      accessToken: null,
      refreshToken: null,
      guestId: getPlayerId(),
      isGuest: true,
      
      loginWithGoogle: async () => {
        set({ isLoading: true, error: null });
        
        try {
          if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android") {
            await ensureSocialLogin();
            const result = await SocialLogin.login({
              provider: "google",
              options: {
                filterByAuthorizedAccounts: false,
              },
            });

            const idToken = (result?.result as { idToken?: string })?.idToken;
            if (!idToken) {
              throw new Error("Login Google falhou (idToken ausente)");
            }

            const response = await apiFetch("/api/auth/google/native", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ idToken, guestId: get().guestId, deviceInfo: getDeviceInfo() }),
            });

            if (!response.ok) {
              throw new Error(await getErrorMessage(response, "Erro no login"));
            }

            const { user, accessToken, refreshToken } = await response.json();
            
            // Sincronizar PLAYER_ID para cross-save
            if (user?.odysId) {
              await syncPlayerIdWithAuth(user.odysId);
            }
            
            set({
              user,
              isAuthenticated: true,
              isGuest: false,
              isLoading: false,
              guestId: user?.odysId || get().guestId,
              accessToken: accessToken || null,
              refreshToken: refreshToken || null,
            });
            return;
          }

          const state = Math.random().toString(36).substring(2, 15);
          sessionStorage.setItem('oauth_state', state);
          
          const { code, state: returnedState } = await openOAuthPopup(
            `/api/auth/google?state=${state}`,
            'Google Login'
          );
          
          if (returnedState !== state) {
            throw new Error('Estado OAuth inválido');
          }
          
          const { user: currentUser } = get();
          const response = await apiFetch('/api/auth/google/callback', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
            body: JSON.stringify({ 
              code, 
              state: returnedState, 
              guestId: get().guestId, 
              deviceInfo: getDeviceInfo(),
              currentOdysId: currentUser?.odysId, // Link to existing account if logged in
            }),
          });
          
          if (!response.ok) {
            throw new Error(await getErrorMessage(response, `Erro no login (${response.status})`));
          }
          
          const { user, accessToken, refreshToken } = await response.json();
          
          // Sincronizar PLAYER_ID para cross-save
          if (user?.odysId) {
            await syncPlayerIdWithAuth(user.odysId);
          }
          
          set({
            user,
            isAuthenticated: true,
            isGuest: false,
            isLoading: false,
            guestId: user?.odysId || get().guestId,
            accessToken: accessToken || null,
            refreshToken: refreshToken || null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
          });
        }
      },
      
      loginWithDiscord: async () => {
        set({ isLoading: true, error: null });
        
        try {
          const state = Math.random().toString(36).substring(2, 15);
          sessionStorage.setItem('oauth_state', state);
          
          const { code, state: returnedState } = await openOAuthPopup(
            `/api/auth/discord?state=${state}`,
            'Discord Login'
          );
          
          if (returnedState !== state) {
            throw new Error('Estado OAuth inválido');
          }
          
          const { user: currentUser } = get();
          const response = await apiFetch('/api/auth/discord/callback', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
            body: JSON.stringify({ 
              code, 
              state: returnedState, 
              guestId: get().guestId, 
              deviceInfo: getDeviceInfo(),
              currentOdysId: currentUser?.odysId, // Link to existing account if logged in
            }),
          });
          
          if (!response.ok) {
            throw new Error(await getErrorMessage(response, `Erro no login (${response.status})`));
          }
          
          const { user, accessToken, refreshToken } = await response.json();
          
          // Sincronizar PLAYER_ID para cross-save
          if (user?.odysId) {
            await syncPlayerIdWithAuth(user.odysId);
          }
          
          set({
            user,
            isAuthenticated: true,
            isGuest: false,
            isLoading: false,
            guestId: user?.odysId || get().guestId,
            accessToken: accessToken || null,
            refreshToken: refreshToken || null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
          });
        }
      },

      loginWithApple: async () => {
        set({ isLoading: true, error: null });
        
        try {
          const state = Math.random().toString(36).substring(2, 15);
          sessionStorage.setItem('oauth_state', state);
          
          const { code, state: returnedState } = await openOAuthPopup(
            `/api/auth/apple?state=${state}`,
            'Apple Login'
          );
          
          if (returnedState !== state) {
            throw new Error('Estado OAuth inválido');
          }
          
          const response = await apiFetch('/api/auth/apple/callback', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, state: returnedState, guestId: get().guestId, deviceInfo: getDeviceInfo() }),
          });
          
          if (!response.ok) {
            throw new Error(await getErrorMessage(response, `Erro no login (${response.status})`));
          }
          
          const { user, accessToken, refreshToken } = await response.json();
          
          // Sincronizar PLAYER_ID para cross-save
          if (user?.odysId) {
            await syncPlayerIdWithAuth(user.odysId);
          }
          
          set({
            user,
            isAuthenticated: true,
            isGuest: false,
            isLoading: false,
            guestId: user?.odysId || get().guestId,
            accessToken: accessToken || null,
            refreshToken: refreshToken || null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
          });
        }
      },

      requestEmailLoginCode: async (email: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiFetch('/api/auth/guest/request-code', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });

          if (!response.ok) {
            throw new Error(await getErrorMessage(response, `Erro ao enviar código (${response.status})`));
          }

          set({
            isLoading: false,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
          });
        }
      },

      verifyEmailLoginCode: async (email: string, code: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiFetch('/api/auth/guest/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, code, deviceInfo: getDeviceInfo() }),
          });

          if (!response.ok) {
            throw new Error(await getErrorMessage(response, `Erro no login (${response.status})`));
          }

          const { user, accessToken, refreshToken } = await response.json();
          
          // Sincronizar PLAYER_ID para cross-save
          if (user?.odysId) {
            await syncPlayerIdWithAuth(user.odysId);
          }
          
          set({
            user,
            isAuthenticated: true,
            isGuest: false,
            isLoading: false,
            guestId: user?.odysId || get().guestId,
            accessToken: accessToken || null,
            refreshToken: refreshToken || null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
          });
        }
      },

      registerGuestProfile: async ({ email, fullName, alias, birthDate }) => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiFetch('/api/auth/guest', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              fullName,
              alias,
              birthDate,
              guestId: get().guestId,
              deviceInfo: getDeviceInfo(),
            }),
          });

          if (!response.ok) {
            throw new Error(await getErrorMessage(response, `Erro no cadastro (${response.status})`));
          }

          const { user, accessToken, refreshToken } = await response.json();
          
          // Sincronizar PLAYER_ID para cross-save
          if (user?.odysId) {
            await syncPlayerIdWithAuth(user.odysId);
          }
          
          set({
            user,
            isAuthenticated: true,
            isGuest: false,
            isLoading: false,
            guestId: user?.odysId || get().guestId,
            accessToken: accessToken || null,
            refreshToken: refreshToken || null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
          });
        }
      },
      
      continueAsGuest: () => {
        set({
          user: null,
          isAuthenticated: false,
          isGuest: true,
          isLoading: false,
          error: null,
          accessToken: null,
          refreshToken: null,
        });
      },
      
      logout: () => {
        apiFetch('/api/auth/logout', { method: 'POST', headers: getAuthHeaders() }).catch(() => {});
        set({
          user: null,
          isAuthenticated: false,
          isGuest: false, // false: user logged out explicitly, should show login options
          isLoading: false,
          error: null,
          accessToken: null,
          refreshToken: null,
        });
      },
      
      linkAccount: async (provider: AuthProvider) => {
        const { user } = get();
        if (!user) return;
        
        set({ isLoading: true, error: null });
        
        try {
          const state = Math.random().toString(36).substring(2, 15);
          sessionStorage.setItem('oauth_state', state);
          
          const { code, state: returnedState } = await openOAuthPopup(
            `/api/auth/${provider}?state=${state}&link=true`,
            `Link ${provider}`
          );
          
          if (returnedState !== state) {
            throw new Error('Estado OAuth inválido');
          }
          
          const response = await apiFetch(`/api/auth/${provider}/link`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
            body: JSON.stringify({ code }),
          });
          
          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Vinculação temporariamente desativada');
          }
          
          const { user: updatedUser } = await response.json();
          
          set({
            user: updatedUser,
            isLoading: false,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Erro desconhecido',
          });
        }
      },
      
      refreshUser: async () => {
        const { accessToken, refreshToken } = get();

        try {
          const response = await apiFetch('/api/auth/me', {
            headers: getAuthHeaders(),
          });
          
          if (response.ok) {
            const { user } = await response.json();
            
            // Sincronizar PLAYER_ID para cross-save
            if (user?.odysId) {
              await syncPlayerIdWithAuth(user.odysId);
            }
            
            set({
              user,
              isAuthenticated: true,
              isGuest: false,
              guestId: user?.odysId || get().guestId,
            });
          } else {
            // Rate limiting não significa sessão inválida; manter estado atual e tentar depois.
            if (response.status === 429) {
              console.warn('[Auth] /api/auth/me rate-limited, mantendo sessão atual');
              return;
            }

            const shouldAttemptRefresh = refreshToken || !Capacitor.isNativePlatform();
            if (shouldAttemptRefresh) {
              const refreshRes = await apiFetch('/api/auth/refresh', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken, deviceInfo: getDeviceInfo() }),
              });

              if (refreshRes.status === 429) {
                console.warn('[Auth] /api/auth/refresh rate-limited, mantendo sessão atual');
                return;
              }

              if (refreshRes.ok) {
                const refreshed = await refreshRes.json();
                set({
                  accessToken: refreshed.accessToken || accessToken || null,
                  refreshToken: refreshed.refreshToken || refreshToken || null,
                });

                const retry = await apiFetch('/api/auth/me', { headers: getAuthHeaders() });
                if (retry.status === 429) {
                  console.warn('[Auth] /api/auth/me (retry) rate-limited, mantendo sessão atual');
                  return;
                }
                if (retry.ok) {
                  const { user } = await retry.json();
                  
                  // Sincronizar PLAYER_ID para cross-save
                  if (user?.odysId) {
                    await syncPlayerIdWithAuth(user.odysId);
                  }
                  
                  set({
                    user,
                    isAuthenticated: true,
                    isGuest: false,
                    guestId: user?.odysId || get().guestId,
                  });
                  return;
                }
              }
            }

            set({
              user: null,
              isAuthenticated: false,
              isGuest: true,
              accessToken: null,
              refreshToken: null,
            });
          }
        } catch (error) {
          console.error('Failed to refresh user:', error);
        }
      },
      
      clearError: () => {
        set({ error: null });
      },
      
      migrateGuestToAccount: async () => {
        const { user, guestId } = get();
        if (!user) return false;
        
        try {
          const response = await apiFetch('/api/auth/migrate', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeaders(),
            },
            body: JSON.stringify({ guestId }),
          });
          
          return response.ok;
        } catch (error) {
          console.error('Migration failed:', error);
          return false;
        }
      },

      updateProfile: (data: Partial<User>) => {
        const { user } = get();
        if (!user) return;
        set({ user: { ...user, ...data } });
      },
    }),
    {
      name: 'rocketanks-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        guestId: state.guestId,
        isGuest: state.isGuest,
        // Persistir tokens apenas em mobile (web usa cookies httpOnly)
        ...(Capacitor.isNativePlatform() ? {
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
        } : {}),
      }),
    }
  )
);

// Hook para obter o ID do usuário (compatível com sistema legado)
export const usePlayerId = () => {
  const { user, guestId, isAuthenticated } = useAuth();
  return isAuthenticated && user ? user.odysId : guestId;
};

// Helper para obter headers de autenticação
// Em mobile: usa Bearer token armazenado no state
// Em web: cookies httpOnly são enviados automaticamente (credentials: 'include')
export const getAuthHeaders = (): Record<string, string> => {
  if (!Capacitor.isNativePlatform()) {
    // Web: cookies são enviados automaticamente, não precisa de header
    return {};
  }
  // Mobile: usa Bearer token
  const token = useAuth.getState().accessToken;
  if (!token) return {};
  return { 'Authorization': `Bearer ${token}` };
};

// [MOBILE] Registra o provedor de token no apiFetch: TODA chamada de API do
// app nativo passa a levar Authorization automaticamente (cookies cross-site
// não chegam no WebView; antes só uma fração dos endpoints usava
// getAuthHeaders manualmente e o restante falhava autenticação no Android).
setAuthTokenProvider(() =>
  Capacitor.isNativePlatform() ? useAuth.getState().accessToken : null,
);
